"use client";

import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { useAuth } from "@/hooks/auth";
import { usePostHog } from "@posthog/next";
import { api } from "@repo/backend/api";
import { applyCanonicalIdentity } from "@repo/analytics";
import { webAnalyticsSession } from "@/lib/briefpanel-analytics-storage";
import { useAnalytics } from "@/components/analytics/use-analytics";
import {
  INTERNAL_SUPER_PROPERTY,
  isInternalTraffic,
} from "@/lib/internal-traffic";

/**
 * Keeps PostHog and Convex analytics identity in sync with the authenticated user.
 */
export function AnalyticsIdentity() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const posthog = usePostHog();
  const { reset } = useAnalytics();
  const linkVisitorToUser = useMutation(api.analytics.identity.linkVisitorToUser);
  const lastIdentifiedId = useRef<string | null>(null);
  const lastLinkKey = useRef<string | null>(null);
  const linkInFlight = useRef<Promise<void> | null>(null);
  const linkGeneration = useRef(0);
  const [resolvedVisitorId, setResolvedVisitorId] = useState<string | null>(() =>
    webAnalyticsSession.peekVisitorId(),
  );

  useEffect(() => {
    let cancelled = false;
    const search = typeof window !== "undefined" ? window.location.search : "";
    void webAnalyticsSession.resolveVisitorId(search).then((id) => {
      if (!cancelled) {
        setResolvedVisitorId(id);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const isRealUser =
      isAuthenticated && user && !user.isAnonymous && user._id;

    if (isRealUser) {
      const userId = user._id;
      const linkKey = `${userId}:${user.email ?? ""}`;

      if (lastLinkKey.current === linkKey && lastIdentifiedId.current === userId) {
        if (posthog) {
          applyCanonicalIdentity(posthog, {
            userId,
            traits: user.email ? { email: user.email } : undefined,
          });
        }
        return;
      }

      const visitorId = resolvedVisitorId;
      if (!visitorId) {
        return;
      }

      const generation = linkGeneration.current;
      const linkAndIdentify = async () => {
        try {
          await linkVisitorToUser({ visitorId, platform: "web" });
        } catch {
          // Leave refs unset so a later render retries the link.
          return;
        }
        if (generation !== linkGeneration.current) {
          return;
        }
        lastLinkKey.current = linkKey;
        lastIdentifiedId.current = userId;
        if (!posthog) {
          return;
        }
        applyCanonicalIdentity(posthog, {
          userId,
          traits: user.email ? { email: user.email } : undefined,
        });
        if (generation !== linkGeneration.current) {
          return;
        }
      };

      linkInFlight.current = linkAndIdentify().finally(() => {
        linkInFlight.current = null;
      });
      return;
    }

    if (lastIdentifiedId.current) {
      linkGeneration.current += 1;
      lastLinkKey.current = null;
      const finalizeSignOut = () => {
        reset();
        // `reset()` returns PostHog to an anonymous identity. Do not identify it
        // with `bp_vid`: a later user identify would attempt to merge an already
        // identified visitor person.
        // `reset()` clears super properties; re-stamp internal traffic so
        // post-logout testing stays excluded from analytics.
        if (isInternalTraffic()) {
          posthog?.register({ [INTERNAL_SUPER_PROPERTY]: true });
        }
        lastIdentifiedId.current = null;
      };
      if (linkInFlight.current) {
        void linkInFlight.current.finally(finalizeSignOut);
      } else {
        finalizeSignOut();
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    posthog,
    reset,
    linkVisitorToUser,
    resolvedVisitorId,
  ]);

  return null;
}
